// Shared metric definitions and helpers for Statistics v2.
// Keep in sync between chart, table, and store.

export const METRIC_OPTIONS = [
  { key: "e1rm", labelKey: "e1rm-string" },
  { key: "stressIndex", labelKey: "stressIndex-string" },
  { key: "tonnage", labelKey: "tonnage-string" },
  { key: "intensityPercentage", labelKey: "intensityPercentage-string" },
  { key: "numberOfLifts", labelKey: "numberOfLifts-string" },
  { key: "numberOfSets", labelKey: "numberOfSets-string" },
  { key: "none", labelKey: "statisticsMetricNone-string" },
];

export const METRIC_TOOLTIPS = {
  e1rm: "statsMetricE1RMTooltip-string",
  stressIndex: "statsMetricStressIndexTooltip-string",
  tonnage: "statsMetricTonnageTooltip-string",
  intensityPercentage: "statsMetricIntensityPctTooltip-string",
  numberOfLifts: "statsMetricNumberOfLiftsTooltip-string",
  numberOfSets: "statsMetricNumberOfSetsTooltip-string",
};

export const getMetricValue = (point, metricKey) => {
  if (!point) return 0;
  if (metricKey === "none") return null;
  if (metricKey === "e1rm") return Number(point.e1rm ?? 0) || 0;
  if (metricKey === "stressIndex") return Number(point.stressIndex ?? 0) || 0;
  if (metricKey === "tonnage") return Number(point.tonnage ?? 0) || 0;
  if (metricKey === "intensityPercentage")
    return Number(point.averageIntensity ?? 0) || 0;
  if (metricKey === "numberOfLifts")
    return Number(point.numberOfLifts ?? 0) || 0;
  if (metricKey === "numberOfSets") return Number(point.numberOfSets ?? 0) || 0;
  if (metricKey === "exertionLoad") return Number(point.exertionLoad ?? 0) || 0;
  return 0;
};

export const defaultMetricByType = (allowStressIndex = true) => ({
  line: "e1rm",
  area: allowStressIndex ? "stressIndex" : "intensityPercentage",
  bar: "tonnage",
});

export const filterMetricOptions = (
  allowStressIndex = true,
  metricOptions = METRIC_OPTIONS
) =>
  (metricOptions ?? []).filter(
    (option) => allowStressIndex || option.key !== "stressIndex"
  );

export const coerceMetricKey = (
  value,
  fallback,
  metricOptions = METRIC_OPTIONS
) => {
  const allowedKeys = new Set((metricOptions ?? []).map((m) => m.key));
  if (allowedKeys.has(value)) return value;
  if (allowedKeys.has(fallback)) return fallback;
  if (allowedKeys.has("e1rm")) return "e1rm";
  return (metricOptions ?? [])[0]?.key ?? "none";
};

export const sanitizeMetricByType = (
  metricByType,
  { allowStressIndex = true, metricOptions = METRIC_OPTIONS } = {}
) => {
  const filtered = filterMetricOptions(allowStressIndex, metricOptions);
  const fallbackArea = allowStressIndex ? "stressIndex" : "intensityPercentage";

  const base = metricByType ?? {};
  return {
    line: coerceMetricKey(base.line, "e1rm", filtered),
    area: coerceMetricKey(base.area, fallbackArea, filtered),
    bar: coerceMetricKey(base.bar, "tonnage", filtered),
  };
};

export const resolveSelectedMetricOptions = (
  metricByType,
  { allowStressIndex = true, metricOptions = METRIC_OPTIONS } = {}
) => {
  const filtered = filterMetricOptions(allowStressIndex, metricOptions);
  const allowedKeys = new Set(filtered.map((m) => m.key));
  const sanitized = sanitizeMetricByType(metricByType, {
    allowStressIndex,
    metricOptions: filtered,
  });

  const orderedKeys = [sanitized.line, sanitized.area, sanitized.bar];
  const uniqueKeys = [];
  orderedKeys.forEach((key) => {
    if (!key || key === "none") return;
    if (!allowedKeys.has(key)) return;
    if (!uniqueKeys.includes(key)) uniqueKeys.push(key);
  });

  return uniqueKeys
    .map((key) => filtered.find((opt) => opt.key === key))
    .filter(Boolean);
};
